import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// ScreenShare — WebRTC screen sharing via Supabase Realtime for signaling.
//
// Flow:
// 1. Sharer calls getDisplayMedia() to capture screen
// 2. Sharer broadcasts "share-start" with their userId
// 3. Viewers receive "share-start", create a PC, and broadcast "viewer-join"
// 4. Sharer receives "viewer-join", creates a PC for that viewer, adds tracks, sends offer
// 5. Viewer receives offer, creates answer, sends it back
// 6. ICE candidates exchanged via Realtime
// 7. Viewer displays the remote stream
// ---------------------------------------------------------------------------

export class ScreenShare {
  private roomId: string;
  private userId: string;
  private channel: any = null;
  private peerConnections = new Map<string, RTCPeerConnection>();
  private localStream: MediaStream | null = null;
  private onRemoteStream: ((userId: string, stream: MediaStream) => void) | null = null;
  private onRemoteStreamEnd: ((userId: string) => void) | null = null;
  private isSharer = false;

  constructor(roomId: string, userId: string) {
    this.roomId = roomId;
    this.userId = userId;
  }

  setHandlers(
    onStream: (userId: string, stream: MediaStream) => void,
    onEnd: (userId: string) => void
  ) {
    this.onRemoteStream = onStream;
    this.onRemoteStreamEnd = onEnd;
  }

  async init() {
    if (!supabase) return;
    this.channel = supabase.channel(`screenshare:${this.roomId}`);

    this.channel.on("broadcast", { event: "webrtc" }, ({ payload }: { payload: any }) => {
      this.handleSignal(payload);
    });

    this.channel.subscribe((status: string) => {
      // subscribed
    });
  }

  private sendSignal(data: any) {
    if (!this.channel) return;
    this.channel.send({
      type: "broadcast",
      event: "webrtc",
      payload: { ...data, from: this.userId },
    });
  }

  private async handleSignal(data: any) {
    if (data.from === this.userId) return;

    // Viewer receives: sharer started sharing — notify them we want to watch
    if (data.type === "share-start") {
      // Tell the sharer we're here and ready
      this.sendSignal({ type: "viewer-join", to: data.from });
    }

    // Sharer receives: a viewer is ready to receive the stream
    if (data.type === "viewer-join" && this.isSharer && this.localStream) {
      await this.createSharerPC(data.from);
    }

    // Viewer receives: offer from sharer
    if (data.type === "offer") {
      let pc = this.peerConnections.get(data.from);
      if (!pc) {
        await this.createViewerPC(data.from);
        pc = this.peerConnections.get(data.from);
      }
      if (!pc) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.sendSignal({ type: "answer", to: data.from, sdp: answer });
      } catch (e) {
        console.error("Error handling offer:", e);
      }
    }

    // Sharer receives: answer from viewer
    if (data.type === "answer") {
      const pc = this.peerConnections.get(data.from);
      if (!pc) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
      } catch (e) {
        console.error("Error handling answer:", e);
      }
    }

    // ICE candidate
    if (data.type === "ice") {
      const pc = this.peerConnections.get(data.from);
      if (!pc) return;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
      } catch {
        // ignore — may arrive before SDP is set
      }
    }

    // Share stopped
    if (data.type === "share-stop") {
      const pc = this.peerConnections.get(data.from);
      if (pc) {
        pc.close();
        this.peerConnections.delete(data.from);
      }
      this.onRemoteStreamEnd?.(data.from);
    }

    // Re-sync request — a viewer wants to re-establish the peer connection
    // (e.g. after latency, freezing, or buffering). The sharer closes the
    // existing PC for that viewer and creates a fresh one with a new offer.
    if (data.type === "resync" && this.isSharer && this.localStream) {
      const existing = this.peerConnections.get(data.from);
      if (existing) {
        try { existing.close(); } catch { /* ignore */ }
        this.peerConnections.delete(data.from);
      }
      // Small delay so the viewer can finish closing their side before the
      // new offer arrives (otherwise setRemoteDescription can race).
      setTimeout(() => { this.createSharerPC(data.from); }, 50);
    }
  }

  // Viewer creates a PC to receive the sharer's stream
  private async createViewerPC(sharerId: string) {
    if (this.peerConnections.has(sharerId)) return; // already exists

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    pc.ontrack = (event) => {
      if (event.streams[0]) {
        this.onRemoteStream?.(sharerId, event.streams[0]);
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignal({ type: "ice", to: sharerId, candidate: event.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
        pc.close();
        this.peerConnections.delete(sharerId);
        this.onRemoteStreamEnd?.(sharerId);
      }
    };

    this.peerConnections.set(sharerId, pc);
  }

  // Sharer creates a PC for a specific viewer, adds tracks, sends offer
  private async createSharerPC(viewerId: string) {
    if (this.peerConnections.has(viewerId)) return; // already exists
    if (!this.localStream) return;

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    // Add local tracks
    this.localStream.getTracks().forEach((track) => {
      pc.addTrack(track, this.localStream!);
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignal({ type: "ice", to: viewerId, candidate: event.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
        pc.close();
        this.peerConnections.delete(viewerId);
      }
    };

    this.peerConnections.set(viewerId, pc);

    // Create and send offer
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.sendSignal({ type: "offer", to: viewerId, sdp: offer });
    } catch (e) {
      console.error("Error creating offer:", e);
    }
  }

  async startSharing(): Promise<boolean> {
    try {
      this.localStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
      this.isSharer = true;

      // Announce to all viewers that we're sharing
      this.sendSignal({ type: "share-start" });

      // When the user stops sharing via browser UI
      this.localStream.getVideoTracks()[0].onended = () => {
        this.stopSharing();
      };

      return true;
    } catch {
      return false;
    }
  }

  // Called when a viewer sees "share-start" — tells the sharer we're ready
  notifySharer(sharerId: string) {
    this.sendSignal({ type: "viewer-join", to: sharerId });
  }

  // Called by a viewer to request a fresh peer connection from the sharer.
  // Useful when the stream has frozen, buffered, or drifted due to latency.
  // The viewer closes their local PC first, then asks the sharer to rebuild.
  resync(sharerId: string) {
    const existing = this.peerConnections.get(sharerId);
    if (existing) {
      try { existing.close(); } catch { /* ignore */ }
      this.peerConnections.delete(sharerId);
    }
    this.sendSignal({ type: "resync", to: sharerId });
    // Re-announce ourselves as a viewer so the sharer's resync handler and
    // the normal viewer-join path both work even if the sharer hasn't seen
    // us yet (covers late-joiner / dropped-PC edge cases).
    this.sendSignal({ type: "viewer-join", to: sharerId });
  }

  stopSharing() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
    this.isSharer = false;
    this.sendSignal({ type: "share-stop" });
    this.peerConnections.forEach((pc) => pc.close());
    this.peerConnections.clear();
  }

  get isSharing() {
    return this.isSharer;
  }

  disconnect() {
    this.stopSharing();
    this.channel?.unsubscribe();
  }
}
